import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, Heart } from 'lucide-react';

/* ----------------------------------------------------------------------------
 * Pricing — two-sided. Candidates are always free; recruiters pay for the pool,
 * intent filters, outreach, and pipeline. Blue brand accent, editorial serif
 * headings, hairline-bordered cards. Recommended tier gets a blue border + a
 * small "Most popular" badge. No purple. No fabricated numbers.
 * -------------------------------------------------------------------------- */

const BLUE = '#2563EB';

const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-80px' },
};

const plans = [
    {
        name: 'Free',
        price: 'Free',
        period: '',
        description: 'Search the pool and start your first role. No card required.',
        features: [
            'Search the candidate pool',
            '10 candidate reveals / mo',
            '1 active role',
            'Basic filters',
        ],
        notIncluded: ['Intent filters', 'Warm outreach', 'ATS sync'],
        cta: 'Start free',
        popular: false,
    },
    {
        name: 'Startup',
        price: '$249',
        period: '/mo',
        description: 'For a team making a handful of hires this quarter.',
        features: [
            'Unlimited pool search',
            'Intent filters (who’s open now)',
            'Lightweight pipeline',
            'Warm outreach',
            '1 ATS sync',
            '2 seats',
        ],
        notIncluded: ['Advanced intent filters', 'Analytics'],
        cta: 'Choose Startup',
        popular: true,
    },
    {
        name: 'Growth',
        price: '$799',
        period: '/mo',
        description: 'More seats, more roles, and deeper signal.',
        features: [
            'Everything in Startup',
            'More seats, roles & outreach',
            'Advanced intent filters',
            'Hiring analytics',
            'Integrations',
        ],
        notIncluded: [],
        cta: 'Choose Growth',
        popular: false,
    },
    {
        name: 'Enterprise',
        price: 'Talk to us',
        period: '',
        description: 'SSO, compliance, and an SLA for larger teams.',
        features: [
            'Everything in Growth',
            'SSO & advanced security',
            'Compliance review',
            'SLA & dedicated support',
        ],
        notIncluded: [],
        cta: 'Talk to us',
        popular: false,
    },
];

const faqs = [
    {
        q: 'Do candidates ever pay?',
        a: 'No. The candidate side is free and always will be. Job seekers set their targets, get matched, and control their own visibility at no cost. Recruiters pay for access to the pool, intent filters, outreach, and pipeline.',
    },
    {
        q: 'How does matching work?',
        a: 'Candidates tell us the roles, seniority, and locations they want. We rank them against your open role by how well their stated targets and skills line up — not by scraping a stale résumé database. You search that pool directly.',
    },
    {
        q: 'How is intent scored?',
        a: 'Intent reflects how actively a candidate is looking right now — things like whether they marked themselves open, how recently they were active, and whether they’re engaging with roles like yours. It tells you who’s open today, not just who exists. It is never a measure of someone’s ability or worth.',
    },
    {
        q: 'What does “warm outreach” mean?',
        a: 'You’re reaching people who already raised their hand by joining the pool and marking themselves open — so your message lands as a welcome reply, not a cold blast. It is not mass-spamming passive profiles who never opted in.',
    },
    {
        q: 'How do you handle data and privacy?',
        a: 'Candidate contact details stay masked until a recruiter reveals them, and candidates control their own visibility and can remove themselves anytime. We never sell candidate data. See our trust & privacy page for the full picture.',
    },
    {
        q: 'Can I cancel anytime?',
        a: 'Yes. Paid plans are month-to-month with no long-term contract and no dark patterns — cancel from your dashboard whenever you like, and you can always fall back to the Free plan.',
    },
];

function Pricing() {
    const handleCheckout = async (priceId, plan) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            console.log('Initiating checkout with API URL:', apiUrl);

            const response = await fetch(`${apiUrl}/api/payments/create-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ priceId, plan }),
            });

            const data = await response.json();

            if (data.url) {
                window.location.href = data.url;
            } else {
                console.error('No URL returned from checkout session creation');
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
        }
    };

    const buttonClasses = (popular) =>
        `block w-full text-center py-3 rounded-lg text-base font-semibold transition-colors mt-auto ${
            popular
                ? 'text-white'
                : 'border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-gray-600'
        }`;

    // Tiers with a real subscription price route through Stripe checkout; tiers
    // without a price ID (Free, Enterprise) route to register / contact.
    const isPaid = (plan) => plan.price.startsWith('$');

    return (
        <div className="bg-[#F8FAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100 min-h-screen">
            {/* ===== HERO ===== */}
            <section className="border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-white to-[#F8FAFC] dark:from-gray-950 dark:to-gray-950">
                <div className="mx-auto max-w-7xl px-6 pt-24 pb-16 text-center lg:px-8">
                    <motion.div {...reveal} transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.05em] text-gray-500">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: BLUE }} />
                        Pricing
                    </motion.div>
                    <motion.h1 {...reveal} transition={{ duration: 0.55, delay: 0.05 }}
                        className="mt-6 font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-gray-900 dark:text-white sm:text-5xl">
                        Pricing for recruiters.<br />Always free for candidates.
                    </motion.h1>
                    <motion.p {...reveal} transition={{ duration: 0.55, delay: 0.1 }}
                        className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
                        Recruiters pay for the pool, intent filters, warm outreach, and pipeline. Job seekers never pay a cent.
                    </motion.p>
                </div>
            </section>

            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                {/* ===== CANDIDATES-ARE-FREE BANNER ===== */}
                <motion.div {...reveal} transition={{ duration: 0.5 }}
                    className="-mt-8 mb-10 relative z-10 rounded-2xl border bg-white dark:bg-gray-900 p-6 sm:p-7"
                    style={{ borderColor: '#BFDBFE' }}>
                    <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl" style={{ background: '#EFF4FE', color: BLUE }}>
                                <Heart size={18} strokeWidth={1.8} />
                            </div>
                            <div>
                                <h2 className="font-serif text-xl font-semibold text-gray-900 dark:text-white">
                                    For candidates: always free.
                                </h2>
                                <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                                    Set your targets, get matched to real fits, and control your own visibility — at no cost, no card, no catch.
                                </p>
                            </div>
                        </div>
                        <Link
                            to="/for-candidates"
                            className="inline-flex flex-none items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-colors"
                            style={{ background: BLUE }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#1D4FD8')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = BLUE)}
                        >
                            For candidates <ArrowRight size={16} />
                        </Link>
                    </div>
                </motion.div>

                <motion.div {...reveal} transition={{ duration: 0.5 }} className="mb-7 text-center">
                    <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>For recruiters</div>
                    <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
                        Plans that scale with your hiring.
                    </h2>
                </motion.div>

                {/* ===== PRICING CARDS ===== */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                    {plans.map((plan, index) => (
                        <motion.div
                            key={plan.name}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.5, delay: index * 0.08 }}
                            className={`relative flex h-full flex-col rounded-2xl bg-white dark:bg-gray-900 p-8 transition-colors ${
                                plan.popular
                                    ? 'border-2 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_48px_-24px_rgba(15,23,42,0.18)]'
                                    : 'border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                            }`}
                            style={plan.popular ? { borderColor: '#BFDBFE' } : undefined}
                        >
                            {plan.popular && (
                                <div
                                    className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                                    style={{ background: '#EFF4FE', color: '#1D4FD8', border: '1px solid #BFDBFE' }}
                                >
                                    Most popular
                                </div>
                            )}

                            <div className="mb-8">
                                <h3 className="font-serif text-xl font-semibold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                                <div className="flex items-baseline gap-1">
                                    <span className="font-serif text-4xl font-semibold text-gray-900 dark:text-white">
                                        {plan.price}
                                    </span>
                                    <span className="text-gray-500 dark:text-gray-400">{plan.period}</span>
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 min-h-[2.5rem]">{plan.description}</p>
                            </div>

                            <div className="mb-8 space-y-4">
                                {plan.features.map((feature, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <Check size={16} className="mt-0.5 flex-shrink-0" style={{ color: BLUE }} />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                                    </div>
                                ))}
                                {plan.notIncluded.map((feature, i) => (
                                    <div key={i} className="flex items-start gap-3 opacity-60">
                                        <X size={16} className="mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-600" />
                                        <span className="text-sm text-gray-400 dark:text-gray-500">{feature}</span>
                                    </div>
                                ))}
                            </div>

                            {isPaid(plan) ? (
                                <button
                                    onClick={() => handleCheckout(null, plan.name.toLowerCase())}
                                    className={buttonClasses(plan.popular)}
                                    style={plan.popular ? { background: BLUE } : undefined}
                                    onMouseEnter={plan.popular ? (e) => (e.currentTarget.style.background = '#1D4FD8') : undefined}
                                    onMouseLeave={plan.popular ? (e) => (e.currentTarget.style.background = BLUE) : undefined}
                                >
                                    {plan.cta}
                                </button>
                            ) : (
                                <Link
                                    to={plan.name === 'Enterprise' ? '/contact' : '/register'}
                                    className={buttonClasses(plan.popular)}
                                    style={plan.popular ? { background: BLUE } : undefined}
                                    onMouseEnter={plan.popular ? (e) => (e.currentTarget.style.background = '#1D4FD8') : undefined}
                                    onMouseLeave={plan.popular ? (e) => (e.currentTarget.style.background = BLUE) : undefined}
                                >
                                    {plan.cta}
                                </Link>
                            )}
                        </motion.div>
                    ))}
                </div>

                {/* ===== PER-HIRE ALTERNATIVE ===== */}
                <motion.div {...reveal} transition={{ duration: 0.5 }}
                    className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 md:p-10 mb-24">
                    <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                        <div className="max-w-2xl">
                            <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: BLUE }}>An alternative</div>
                            <h3 className="mt-3 font-serif text-2xl font-semibold text-gray-900 dark:text-white">
                                Prefer to pay per hire?
                            </h3>
                            <p className="mt-3 leading-relaxed text-gray-600 dark:text-gray-300">
                                Instead of a monthly subscription, you can pay a flat <span className="font-semibold text-gray-900 dark:text-white">$2,500 success fee per hire</span> — you only pay when someone you found through AutoHire actually joins your team.
                            </p>
                        </div>
                        <Link
                            to="/contact"
                            className="inline-flex flex-none items-center justify-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-3 text-base font-semibold text-gray-900 dark:text-white transition-colors hover:border-gray-400 dark:hover:border-gray-600"
                        >
                            Talk to us about per-hire <ArrowRight size={16} />
                        </Link>
                    </div>
                </motion.div>

                {/* ===== FAQ ===== */}
                <div className="max-w-3xl mx-auto pb-24">
                    <motion.h2 {...reveal} transition={{ duration: 0.5 }}
                        className="font-serif text-3xl font-semibold tracking-tight text-center text-gray-900 dark:text-white mb-12">
                        Frequently asked questions
                    </motion.h2>
                    <div className="space-y-4">
                        {faqs.map((item, i) => (
                            <motion.div
                                key={i}
                                {...reveal}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 transition-colors hover:border-gray-300 dark:hover:border-gray-700"
                            >
                                <h4 className="font-serif text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.q}</h4>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{item.a}</p>
                            </motion.div>
                        ))}
                    </div>
                    <motion.p {...reveal} transition={{ duration: 0.5 }}
                        className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        Want the full data &amp; privacy picture? Read our{' '}
                        <Link to="/trust" className="font-semibold" style={{ color: BLUE }}>trust &amp; privacy page</Link>.
                    </motion.p>
                </div>

                {/* ===== CTA ===== */}
                <section className="rounded-2xl bg-[#0F172A] mb-20">
                    <div className="mx-auto max-w-3xl px-6 py-16 text-center lg:px-8">
                        <motion.div {...reveal} transition={{ duration: 0.55 }}>
                            <h2 className="font-serif text-3xl font-semibold leading-tight text-white sm:text-4xl">
                                Start with candidates who want your role.
                            </h2>
                            <p className="mx-auto mt-4 max-w-xl text-lg text-gray-300">
                                Search the pool free. Upgrade when your hiring volume needs it — month-to-month, cancel anytime.
                            </p>
                            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                                <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-lg px-7 py-3.5 text-base font-semibold text-white" style={{ background: BLUE }}>
                                    Start free <ArrowRight size={17} />
                                </Link>
                                <Link to="/contact" className="inline-flex items-center justify-center rounded-lg border border-white/20 bg-transparent px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10">
                                    Contact sales
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default Pricing;
